<template>
  <v-row>
    <v-col cols="12">
      <v-card class="qt-card" flat>
        <div class="qt-header pl-base-bg">
          <div class="qt-header-left">
            <div class="qt-header-icon">
              <v-icon color="#fff" size="22">mdi-file-document-edit-outline</v-icon>
            </div>
            <div>
              <div class="qt-title">Quotation #{{ quotation?.quotationNumber }}</div>
              <div class="qt-subtitle">
                Created on {{ formatDate(quotation?.quotationDate) }}
                <span v-if="quotation?.validUntil"> &middot; Valid until {{ formatDate(quotation?.validUntil) }}</span>
              </div>
            </div>
          </div>

          <div class="qt-header-right">
            <v-chip
              :color="statusColor(quotation?.status)"
              dark
              small
              class="text-uppercase font-weight-bold qt-status-chip"
            >
              {{ isExpired ? "EXPIRED" : quotation?.status }}
            </v-chip>

            <div class="qt-actions">
              <v-tooltip v-if="document" bottom>
                <template #activator="{ on, attrs }">
                  <v-btn
                    v-bind="attrs"
                    v-on="on"
                    icon
                    small
                    class="qt-action-btn qt-action-btn--view"
                    :href="document.fileUrl"
                    target="_blank"
                  >
                    <v-icon small color="#fff">mdi-eye</v-icon>
                  </v-btn>
                </template>
                <span>View document</span>
              </v-tooltip>

              <v-btn
                v-if="quotation?.status !== 'CONVERTED' && !isViewOnly"
                small
                class="qt-edit-btn"
                :to="`/quotations/${quotation?._id}/edit`"
              >
                Edit
              </v-btn>

              <router-link
                v-if="quotation?.status === 'CONVERTED'"
                :to="`/invoices/${quotation.convertedInvoiceId}/view`"
                class="qt-converted-link"
              >
                View resulting Invoice
              </router-link>

              <v-btn
                v-else-if="!isViewOnly"
                small
                class="qt-convert-btn"
                @click="convertToInvoice"
              >
                Convert to Invoice
              </v-btn>
            </div>
          </div>
        </div>

        <div class="pa-6">
          <v-alert v-if="isExpired" type="warning" outlined dense border="left" class="mb-4">
            This quotation's validity has passed. You can still convert it if the customer wants to honor it.
          </v-alert>

          <v-row>
            <v-col cols="12" md="6">
              <div class="qt-party-card">
                <div class="qt-party-label">Quotation For</div>
                <div class="qt-party-name">{{ quotation?.customer?.name }}</div>
                <div class="qt-party-line" v-if="quotation?.customer?.address">
                  {{ quotation?.customer?.address }}
                </div>
                <div class="qt-party-line" v-if="quotation?.customer?.gstin">
                  GSTIN: {{ quotation?.customer?.gstin }}
                </div>
              </div>
            </v-col>

            <v-col cols="12" md="6">
              <div class="qt-party-card">
                <div class="qt-party-label">From</div>
                <div class="qt-party-name">{{ quotation?.seller?.businessName }}</div>
                <div class="qt-party-line">{{ quotation?.seller?.address }}</div>
                <div class="qt-party-line" v-if="quotation?.seller?.gstin">
                  GSTIN: {{ quotation?.seller?.gstin }}
                </div>
              </div>
            </v-col>
          </v-row>

          <div class="qt-items-wrap mt-4">
            <div class="table-scroll">
              <v-simple-table dense class="qt-items-table">
                <thead>
                  <tr>
                    <th class="text-left">Item</th>
                    <th class="text-right">HSN / SAC</th>
                    <th class="text-right">Qty</th>
                    <th class="text-right">Rate</th>
                    <th class="text-right">Tax %</th>
                    <th class="text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, i) in quotation?.items" :key="i">
                    <td>{{ item.product }}</td>
                    <td class="text-right">{{ item.hsn }}</td>
                    <td class="text-right">{{ item.quantity }}</td>
                    <td class="text-right">{{ formatAmount(item.price) }}</td>
                    <td class="text-right">{{ item.taxPercent }}%</td>
                    <td class="text-right font-weight-medium">{{ formatAmount(item.amount) }}</td>
                  </tr>
                </tbody>
              </v-simple-table>
            </div>
          </div>

          <v-row justify="end" class="mt-4">
            <v-col cols="12" md="4">
              <div class="qt-totals-card">
                <v-row dense>
                  <v-col class="grey--text py-1">Subtotal</v-col>
                  <v-col class="text-right py-1">{{ formatAmount(quotation?.subtotal) }}</v-col>
                </v-row>
                <v-row dense>
                  <v-col class="grey--text py-1">Estimated Tax</v-col>
                  <v-col class="text-right py-1">{{ formatAmount(quotation?.gst) }}</v-col>
                </v-row>
                <v-divider class="my-2" />
                <v-row dense class="text-h6 font-weight-bold">
                  <v-col>Total</v-col>
                  <v-col class="text-right qt-total-value">{{ formatAmount(quotation?.totalAmount) }}</v-col>
                </v-row>
              </div>
            </v-col>
          </v-row>

          <v-row v-if="quotation?.note" class="mt-4">
            <v-col>
              <v-alert type="info" outlined dense>{{ quotation.note }}</v-alert>
            </v-col>
          </v-row>
        </div>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
export default {
  name: "ViewQuotation",

  data() {
    return {
      quotation: null,
      document: null,
      isExpired: false,
    };
  },

  async created() {
    await this.fetchQuotation();
  },

  methods: {
    async fetchQuotation() {
      try {
        const id = this.$route.params.id;
        const res = await this.$store.dispatch("quotations/getQuotationById", id);
        this.quotation = res.quotation;
        this.document = res.document;
        this.isExpired = res.isExpired;
      } catch (err) {
        this.$toast?.error("Failed to load quotation");
      }
    },

    convertToInvoice() {
      this.$router.push(`/invoices/create?fromQuotation=${this.quotation._id}`);
    },

    statusColor(status) {
      switch (status) {
        case "DRAFT":
          return "grey";
        case "SENT":
          return "blue";
        case "ACCEPTED":
          return "green";
        case "REJECTED":
          return "red";
        case "EXPIRED":
          return "orange";
        case "CONVERTED":
          return "purple";
        default:
          return "grey";
      }
    },

    formatDate(value) {
      if (!value) return "-";
      return new Date(value).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    },

    formatAmount(value) {
      return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        maximumFractionDigits: 2,
      }).format(value || 0);
    },
  },
};
</script>

<style scoped>
.qt-card {
  border-radius: 0;
  overflow: hidden;
  box-shadow: 0 6px 24px rgba(15, 30, 50, 0.1);
}

.qt-header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 20px;
}

.qt-header-left {
  display: flex;
  align-items: center;
  gap: 14px;
}

.qt-header-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.16);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.qt-title {
  font-size: 16px;
  font-weight: 700;
  color: #fff;
  letter-spacing: -0.01em;
}

.qt-subtitle {
  font-size: 11.5px;
  color: rgba(255, 255, 255, 0.75);
  margin-top: 2px;
}

.qt-header-right {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.qt-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.qt-action-btn {
  border: 2px solid rgba(255, 255, 255, 0.6);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15) !important;
}

.qt-action-btn--view {
  background: #f59e0b !important;
}

.qt-edit-btn,
.qt-convert-btn {
  background: rgba(255, 255, 255, 0.16) !important;
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: #ffffff !important;
  font-weight: 700 !important;
  text-transform: none !important;
  box-shadow: none !important;
}

.qt-edit-btn:hover,
.qt-convert-btn:hover {
  background: rgba(255, 255, 255, 0.26) !important;
}

.qt-converted-link {
  color: #fff !important;
  font-weight: 700;
  font-size: 13.5px;
  text-decoration: underline;
}

.qt-party-card {
  background: #f8fafc;
  border: 1px solid #eef1f5;
  border-radius: 0;
  padding: 16px 18px;
  height: 100%;
}

.qt-party-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #94a3b8;
  margin-bottom: 6px;
}

.qt-party-name {
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 4px;
}

.qt-party-line {
  font-size: 13px;
  color: #64748b;
  line-height: 1.6;
}

.qt-items-wrap {
  border: 1px solid #eef1f5;
  border-radius: 0;
  overflow: auto;
}

.table-scroll {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.qt-items-table {
  min-width: 560px;
}

.qt-items-table >>> th {
  background: #f8fafc;
  font-weight: 700 !important;
  font-size: 12px !important;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #64748b !important;
}

.qt-totals-card {
  background: #f8fafc;
  border: 1px solid #eef1f5;
  border-radius: 0;
  padding: 16px 18px;
}

.qt-total-value {
  color: #0a5a90;
}
</style>
